import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import {
  Zap,
  RefreshCw,
  TrendingUp,
  Shield,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const WorkingWallet = () => {
  const [walletBalance, setWalletBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCredited: 0,
    totalDebited: 0,
    todayCredited: 0,
    todayDebited: 0,
    blockedAmount: 0,
    levelIncome: 0,
    adminAdded: 0,
    adminDebited: 0
  });

  const userDbKey = localStorage.getItem('dbKey');
  const userRegistrationDate = localStorage.getItem('registrationDate');
  const userId = localStorage.getItem('userId');
  const userSponsorId = localStorage.getItem('userSponsorId') || localStorage.getItem('sponsorId');

  useEffect(() => {
    if (userDbKey) {
      fetchWorkingWallet();
    }
  }, [userDbKey]);

  const fetchWorkingWallet = async () => {
    try {
      setLoading(true);

      let activationBonusFromUser = null;
      if (userRegistrationDate && userDbKey) {
        const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
        const userSnapshot = await get(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          const workingBalance = parseFloat(userData.wallets?.working) || 0;
          setWalletBalance(workingBalance);
          if (userData.activationBonus?.awarded) {
            activationBonusFromUser = userData.activationBonus;
          }
        }
      }

      const trans = [];
      let totalCredited = 0;
      let totalDebited = 0;
      let todayCredited = 0;
      let todayDebited = 0;
      let blockedAmount = 0;
      let levelIncome = 0;
      let adminAdded = 0;
      let adminDebited = 0;
      const today = new Date().toISOString().split('T')[0];

      try {
        const levelRef = ref(database, 'levelIncomes');
        const levelSnapshot = await get(levelRef);

        if (levelSnapshot.exists()) {
          const data = levelSnapshot.val();
          Object.entries(data).forEach(([date, users]) => {
            if (users[userDbKey]) {
              Object.entries(users[userDbKey]).forEach(([incomeId, income]) => {
                const amount = parseFloat(income.amount) || 0;
                const isBlocked = income.status === 'blocked';

                const transaction = {
                  id: incomeId,
                  date,
                  amount,
                  type: 'level',
                  level: income.level || 1,
                  status: income.status || 'credited',
                  fromUser: income.fromUserName,
                  description: income.description || `Level ${income.level} Commission`,
                  timestamp: income.date || income.createdAt,
                  isBlocked,
                  source: 'Level Income'
                };

                trans.push(transaction);

                if (!isBlocked) {
                  totalCredited += amount;
                  levelIncome += amount;
                  if (date === today) todayCredited += amount;
                } else {
                  blockedAmount += amount;
                }
              });
            }
          });
        }
      } catch (error) {
        console.error('Error fetching level incomes:', error);
      }

      try {
        const adminHistoryRef = ref(database, 'adminaddedhistory');
        const adminHistorySnapshot = await get(adminHistoryRef);

        if (adminHistorySnapshot.exists()) {
          const data = adminHistorySnapshot.val();

          const mapAdminTx = (tx, dateKey, fallbackKey) => {
            if (!tx || typeof tx !== 'object') return;

            const txUserId = String(tx.userId || '').trim();
            const txMemberId = String(tx.memberId || '').trim();
            const txSponsorId = String(tx.sponsorId || '').trim();
            const txWalletType = String(tx.walletType || '').trim();
            const txTypeText = String(tx.transactionType || '').trim().toLowerCase();
            const walletTypeLower = txWalletType.toLowerCase();
            const rawAmount = parseFloat(tx.amount) || 0;
            const amount = Math.abs(rawAmount);
            const isDebit = txTypeText.includes('debit') || rawAmount < 0;
            const isAgPower =
              walletTypeLower === 'ag power wallet' ||
              walletTypeLower === 'av power wallet' ||
              txTypeText.includes('ag power') ||
              txTypeText.includes('av power');
            const isCurrentUser =
              (userId && txUserId === userId) ||
              (userDbKey && txUserId === userDbKey) ||
              (userDbKey && txMemberId === userDbKey) ||
              (userSponsorId && txSponsorId === userSponsorId);

            if (!isAgPower || !isCurrentUser) return;

            const transaction = {
              id: tx.transactionId || fallbackKey,
              date: dateKey,
              amount,
              type: 'admin_added',
              status: isDebit ? 'debited' : 'completed',
              previousBalance: parseFloat(tx.previousBalance) || 0,
              newBalance: parseFloat(tx.newBalance) || 0,
              description: tx.description || tx.transactionType || `AV Power Wallet ${isDebit ? 'Debit' : 'Credit'}`,
              timestamp: tx.date || dateKey,
              addedBy: 'Aerovolt',
              walletType: tx.walletType,
              transactionType: tx.transactionType,
              source: 'Aerovolt',
              isAdminAdded: true,
              isDebit
            };

            trans.push(transaction);
            if (isDebit) {
              totalDebited += amount;
              adminDebited += amount;
              if (dateKey === today) todayDebited += amount;
            } else {
              totalCredited += amount;
              adminAdded += amount;
              if (dateKey === today) todayCredited += amount;
            }
          };

          Object.entries(data).forEach(([dateKey, dateData]) => {
            Object.entries(dateData).forEach(([sponsorId, sponsorTransactions]) => {
              if (Array.isArray(sponsorTransactions)) {
                sponsorTransactions.forEach((tx, index) => {
                  mapAdminTx(tx, dateKey, `admin_${dateKey}_${sponsorId}_${index}`);
                });
              } else if (sponsorTransactions && typeof sponsorTransactions === 'object') {
                mapAdminTx(sponsorTransactions, dateKey, `admin_${dateKey}_${sponsorId}`);
              }
            });
          });
        }
      } catch (error) {
        console.error('Error fetching adminaddedhistory:', error);
      }

      if (activationBonusFromUser?.awarded) {
        const bonusId = activationBonusFromUser.transactionId || 'activation_bonus';
        const alreadyExists = trans.some((tx) => tx.id === bonusId);
        if (!alreadyExists) {
          const bonusAmount = parseFloat(activationBonusFromUser.bonusAmount) || 0;
          const bonusTimestamp = activationBonusFromUser.awardedAt || new Date().toISOString();
          const bonusDateKey = bonusTimestamp.split('T')[0];

          trans.push({
            id: bonusId,
            date: bonusDateKey,
            amount: bonusAmount,
            type: 'activation_bonus',
            status: 'completed',
            previousBalance: activationBonusFromUser.previousBalance || 0,
            newBalance: activationBonusFromUser.newBalance || 0,
            description: activationBonusFromUser.description || 'Activation bonus credited',
            timestamp: bonusTimestamp,
            addedBy: 'Aerovolt',
            walletType: 'AV Power Wallet',
            transactionType: 'AV Power Wallet Credit',
            source: 'Aerovolt',
            isAdminAdded: true,
            isDebit: false
          });

          totalCredited += bonusAmount;
          adminAdded += bonusAmount;
          if (bonusDateKey === today) todayCredited += bonusAmount;
        }
      }

      trans.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setTransactions(trans);
      setStats({
        totalCredited,
        totalDebited,
        todayCredited,
        todayDebited,
        blockedAmount,
        levelIncome,
        adminAdded,
        adminDebited
      });
    } catch (error) {
      console.error('Error fetching working wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status, isAdminAdded, isDebit) => {
    if (isAdminAdded) {
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded text-xs border ${
            isDebit
              ? 'bg-red-500/20 text-red-400 border-red-500/30'
              : 'bg-purple-500/20 text-purple-400 border-purple-500/30'
          }`}
        >
          {isDebit ? <ArrowDownRight className="h-3 w-3 mr-1" /> : <ArrowUpRight className="h-3 w-3 mr-1" />}
          {isDebit ? 'Aerovolt Debited' : 'Aerovolt Added'}
        </span>
      );
    }
    if (status === 'blocked') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
          <Shield className="h-3 w-3 mr-1" />
          Blocked
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
        Credited
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">AV Power Wallet</h1>
      <p className="text-gray-400 mb-6">Your level commission earnings & admin added funds</p>

      <div className="card-glow p-6 mb-6 bg-gradient-to-r from-blue-500/10 to-primary/10 border border-blue-500/30 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white mb-2">Power Wallet Balance</h3>
            <div className="text-4xl font-bold text-blue-400">{formatCurrency(walletBalance)}</div>
            <p className="text-gray-400 text-sm mt-2">Level commissions + Admin additions</p>
          </div>
          <div className="p-4 bg-blue-500/20 rounded-full">
            <Zap className="h-12 w-12 text-blue-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Credited</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalCredited)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Debited</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.totalDebited)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Today's Income</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.todayCredited)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Today's Debits</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.todayDebited)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Level Income</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.levelIncome)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Aerovolt Added</div>
          <div className="text-lg font-bold text-purple-400">{formatCurrency(stats.adminAdded)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Aerovolt Debited</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.adminDebited)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Blocked</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.blockedAmount)}</div>
        </div>
      </div>

      <div className="mb-6">
        <button
          onClick={fetchWorkingWallet}
          className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-bold text-white mb-4">Recent Transactions</h3>

        {transactions.length > 0 ? (
          transactions.map((transaction) => {
            const isBlocked = transaction.status === 'blocked';
            const isAdminAdded = transaction.isAdminAdded;
            const isDebit = transaction.isDebit;

            return (
              <div key={transaction.id} className="card-glow p-4 bg-gray-800/50 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        isAdminAdded
                          ? isDebit
                            ? 'bg-red-500/20'
                            : 'bg-purple-500/20'
                          : isBlocked
                          ? 'bg-red-500/20'
                          : 'bg-blue-500/20'
                      }`}
                    >
                      {isAdminAdded ? (
                        isDebit ? (
                          <ArrowDownRight className="h-5 w-5 text-red-400" />
                        ) : (
                          <ArrowUpRight className="h-5 w-5 text-purple-400" />
                        )
                      ) : (
                        <TrendingUp className={`h-5 w-5 ${isBlocked ? 'text-red-400' : 'text-blue-400'}`} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {transaction.type === 'level' && !isAdminAdded && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            Level {transaction.level}
                          </span>
                        )}
                        {getStatusBadge(transaction.status, isAdminAdded, isDebit)}
                      </div>
                      <p className="text-gray-300 text-sm">{transaction.description}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {isAdminAdded ? (
                          <>{isDebit ? 'Debited by' : 'Added by'}: {transaction.addedBy}</>
                        ) : (
                          <>From: {transaction.fromUser || 'System'}</>
                        )}{' '}
                        | {formatDate(transaction.timestamp)}
                      </p>
                      {isAdminAdded && (
                        <p className="text-gray-500 text-xs mt-1">
                          Previous: {formatCurrency(transaction.previousBalance)} - New: {formatCurrency(transaction.newBalance)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div
                    className={`text-lg font-bold ${
                      isAdminAdded
                        ? isDebit
                          ? 'text-red-400'
                          : 'text-purple-400'
                        : isBlocked
                        ? 'text-red-400'
                        : 'text-green-400'
                    }`}
                  >
                    {isAdminAdded && isDebit ? '-' : '+'}
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
            <div className="text-4xl mb-4">[]</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Transactions</h3>
            <p className="text-gray-400">You have not received level commissions or admin additions yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkingWallet;




